import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../../../components/dashboard-layout/layout";
import SearchInput from "../../../components/form/search-input";
import { PaginationData } from "../../../components/pagination";
import TableComponent, {
  TableHeader,
} from "../../../components/table.component";
import { PATHS } from "../../../constants/path.constants";
import { useForm } from "../../../hooks/use-form.hook";
import {
  AlertType,
  useDashboardStore,
} from "../../dashboard/store/dashboard.store";
import { getUsers } from "../apis/users.api";
import { UserEntity } from "../entities/user.entity";
import { IUserModel, UserModel } from "../models/user.model";

const headers: TableHeader[] = [
  {
    title: "Name",
    contentKey: "name",
  },
  {
    title: "Email",
    contentKey: "email",
  },
  {
    title: "Last Login",
    contentKey: "lastLogin",
  },
];

export default function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initEffect = useRef<boolean>(false);
  const [users, setUsers] = useState<UserEntity[]>([]);
  const setAlert = useDashboardStore((state) => state.setAlert);
  const [paginationData, setPaginationData] = useState<PaginationData>({
    currentPage: 1,
    total: 0,
    perPage: 20,
    totalPages: 0,
  });

  const onSearchClick = () => {
    const search =
      formFunc.values.searchString === "" ? null : formFunc.values.searchString;
    populateUserTable("1", search);
    setSearchParams({ page: "1", ...(search && { search }) });
  };

  const onPaginationClick = (page: number) => {
    const search = searchParams.get("search");
    populateUserTable(page.toString(), search);
    setSearchParams({ page: page.toString(), ...(search && { search }) });
  };

  const populateUserTable = useCallback(
    async (paginatedPageId = "1", searchString?: string | null) => {
      try {
        const response = await getUsers(paginatedPageId, searchString ?? null);
        const users = response.data.data.map((user: IUserModel) => {
          return new UserModel(user).toEntity();
        });
        setUsers(users);
        setPaginationData((data) => ({
          ...data,
          currentPage: response.data.currentPage,
          total: response.data.totalUsers,
          totalPages: response.data.totalPages,
        }));
      } catch (error: any) {
        setAlert({
          message: error.response.data.message,
          type: AlertType.error,
        });
      }
    },
    [setAlert]
  );

  useEffect(() => {
    if (initEffect.current === false) {
      const page = searchParams.get("page");
      const search = searchParams.get("search") ?? null;
      populateUserTable(page ?? undefined, search);
    }
    return () => {
      initEffect.current = true;
    };
  }, [populateUserTable, searchParams]);

  const formFunc = useForm(onSearchClick, {
    searchString: "",
  });

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center">
          <form className="flex flex-1" onSubmit={formFunc.onSubmit}>
            <SearchInput
              id="searchString"
              placeholder="Search Users"
              value={formFunc.values.searchString}
              onChange={formFunc.onChange}
            />
          </form>
        </div>
        <TableComponent
          headers={headers}
          content={users}
          viewHref={PATHS.viewUser}
          paginationData={paginationData}
          onPaginationClick={onPaginationClick}
        />
      </div>
    </Layout>
  );
}
